import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import MedicationPrescriptionModal from '../components/MedicationPrescriptionModal';
import { PainLogs, History, Statistics, DistressHistory } from '@medical/patient-app';
import { translateZoneNameToEnglish } from '@medical/shared/utils/translateZoneName';
import { useTranslation } from 'react-i18next';
import i18n from '@medical/shared/i18n';
import { calculateAge } from '@medical/shared/utils/ageUtils';
import { hasFeature } from '@medical/shared/utils/features';

const EMPTY_PATIENT_FILTERS = { name: '', id: '', status: 'all' };

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
  overflow-x: hidden;
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  background: #0F1F4D;
  color: white;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 200;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    width: 85vw;
    max-width: 320px;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${props => props.isOpen ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none'};
  }

  @media (max-width: 480px) {
    width: 90vw;
    max-width: 300px;
  }
`;

const SidebarProfileSection = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ProfileNavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 8px;
  transition: background 0.2s ease;
  margin-bottom: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ProfileNavAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
  color: white;
`;

const ProfileNavInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileNavName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
  color: white;
`;

const ProfileNavId = styled.div`
  font-size: 0.75rem;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: white;
`;

const ProfileNavToggle = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SidebarTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: white;
`;

const SidebarSubtitle = styled.p`
  font-size: 0.75rem;
  opacity: 0.9;
  margin: 0;
  color: white;
`;

const SidebarTabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0.875rem;
    gap: 0.375rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.25rem;
  }
`;

const SidebarTab = styled.button`
  color: white;
  text-decoration: none;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  width: 100%;
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateX(4px);
  }

  &:active {
    transform: translateX(2px);
  }

  @media (max-width: 768px) {
    padding: 1.125rem 1.25rem;
    font-size: 0.9375rem;
    min-height: 52px;
  }

  @media (max-width: 480px) {
    padding: 1rem 1rem;
    font-size: 0.875rem;
    min-height: 48px;
  }

  ${props => props.active && `
    background-color: rgba(255, 255, 255, 0.25);
    font-weight: 600;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 60%;
      background-color: white;
      border-radius: 0 4px 4px 0;
    }
  `}
`;

const TabBadge = styled.span`
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0;
  min-height: 0;
`;

const PatientSearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SidebarPatientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SidebarPatientItem = styled.div`
  padding: 1rem;
  border: 1px solid ${props => props.selected ? '#059669' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#f0fdf4' : 'white'};
  position: relative;

  &:hover {
    border-color: #059669;
    background: #f0fdf4;
    transform: translateX(4px);
  }

  ${props => props.isNew && `
    border-left: 4px solid #3b82f6;
    background: linear-gradient(to right, #eff6ff, white);
  `}
`;

const PatientItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
`;

const PatientItemName = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 0.9375rem;
  flex: 1;
`;

const NewPatientBadge = styled.span`
  background: #3b82f6;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  white-space: nowrap;
`;

const PatientItemDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PatientItemId = styled.span`
  font-weight: 500;
  color: #059669;
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  max-width: calc(100% - 280px);
  position: relative;
  min-height: 100vh;

  @media (max-width: 768px) {
    margin-left: 0;
    max-width: 100%;
    padding: 0.75rem;
    padding-top: 4rem; /* Space for mobile menu button */
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-top: 3.5rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 201;
  background: #0B63C5;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  min-width: 48px;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    top: 0.75rem;
    left: 0.75rem;
    padding: 0.75rem;
    font-size: 1.25rem;
    border-radius: 10px;
    min-width: 44px;
    min-height: 44px;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 199;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: 768px) {
    display: ${props => props.show ? 'block' : 'none'};
    backdrop-filter: blur(2px);
  }

  @media (max-width: 480px) {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    gap: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    gap: 0.5rem;
    
    h1 {
      font-size: 1.125rem !important;
      line-height: 1.3;
    }
  }
`;

const WelcomeMessage = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 10;

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    right: auto;
    margin-bottom: 1rem;
    text-align: center;
    width: 100%;
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.875rem;
    font-size: 0.875rem;
    border-radius: 10px;
  }
`;

const UserSection = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  text-decoration: none;
  color: white;
  transition: all 0.2s ease;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-height: 44px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    gap: 0.875rem;
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
`;

const UserLabel = styled.div`
  font-size: 0.75rem;
  opacity: 0.9;
`;

const LogoutButton = styled.button`
  width: 100%;
  background: rgba(239, 68, 68, 0.2);
  color: white;
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.6);
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.98);
    background: rgba(239, 68, 68, 0.4);
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
    min-height: 48px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.875rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.625rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 1.125rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 8px;
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  background: ${props => props.color || '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.625rem;
    margin-bottom: 0.375rem;
  }

  @media (max-width: 480px) {
    font-size: 1.375rem;
    margin-bottom: 0.25rem;
  }
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.125rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 1rem;
  min-height: 44px;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 1rem;
    margin-bottom: 0.875rem;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.9375rem;
    margin-bottom: 0.75rem;
    min-height: 44px;
    border-radius: 10px;
  }
`;

const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
  gap: 0.75rem;
  align-items: end;
  margin-bottom: 0.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #475569;

  input, select {
    width: 100%;
    min-height: 44px;
    padding: 0.625rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 400;
    color: #0f172a;
    background: #fff;
  }

  input:focus, select:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
  }
`;

const ClearFiltersButton = styled.button`
  min-height: 44px;
  padding: 0 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:not(:disabled):hover {
    border-color: #059669;
    color: #059669;
  }
`;

const FilterSummary = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  color: #64748b;
`;

const DateSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }
`;

const DateInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  min-width: 200px;
  background: white;
  -webkit-tap-highlight-color: transparent;

  &:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    padding: 0.875rem;
    font-size: 1rem;
    min-height: 48px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.9375rem;
    min-height: 44px;
  }
`;

const DateNavButton = styled.button`
  padding: 0.5rem 1rem;
  background: #059669;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
  min-width: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: #047857;
  }

  &:active {
    transform: scale(0.96);
    background: #065f46;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 48px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.875rem;
    min-height: 44px;
  }
`;

const DateLabel = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PatientList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const PatientCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: #059669;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 1.125rem;
    border-radius: 10px;
    gap: 0.875rem;
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 8px;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
`;

const PatientInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PatientCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;

  @media (max-width: 480px) {
    gap: 0.75rem;
    padding-bottom: 0.875rem;
  }
`;

const PatientAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 1.25rem;
  }
`;

const PatientName = styled.div`
  font-weight: 700;
  font-size: 1.125rem;
  color: #111827;
  margin-bottom: 0.25rem;
  word-break: break-word;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.0625rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    line-height: 1.4;
    margin-bottom: 0.125rem;
  }
`;

const PatientDetails = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PatientDetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    gap: 0.375rem;
  }
`;

const DistressBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${props => {
    if (props.score >= 8) return '#fee2e2';
    if (props.score >= 4) return '#fef3c7';
    if (props.score >= 1) return '#dbeafe';
    return '#d1fae5';
  }};
  color: ${props => {
    if (props.score >= 8) return '#991b1b';
    if (props.score >= 4) return '#92400e';
    if (props.score >= 1) return '#1e40af';
    return '#065f46';
  }};
  border: 1px solid ${props => {
    if (props.score >= 8) return '#fecaca';
    if (props.score >= 4) return '#fde68a';
    if (props.score >= 1) return '#bfdbfe';
    return '#a7f3d0';
  }};
`;


const HighDistressAlert = styled.div`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.9;
    }
  }
`;

const NewPatientIndicator = styled.span`
  display: inline-block;
  background: #3b82f6;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin-left: auto;
`;

const InactivePatientIndicator = styled(NewPatientIndicator)`
  background: #94a3b8;
  margin-left: 0.5rem;
`;

const PatientActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;

  @media (max-width: 768px) {
    gap: 0.625rem;
    padding-top: 0.875rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding-top: 0.75rem;
  }
`;

const ViewButton = styled.button`
  background: #059669;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
  width: 100%;

  &:hover {
    background: #047857;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    min-height: 48px;
    font-weight: 600;
  }
`;


const MessageButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
  width: 100%;

  &:hover {
    background: #2563eb;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    min-height: 48px;
    font-weight: 600;
  }
`;

const MedicationButton = styled.button`
  background: #059669;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 0.5rem;
  min-height: 44px;
  width: 100%;

  &:hover {
    background: #047857;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    margin-right: 0;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
    min-height: 48px;
    font-weight: 600;
  }
`;

const MessageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  @media (max-width: 480px) {
    padding: 0.25rem;
    padding-top: 1rem;
  }
`;

const MessageModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 0.75rem;
    max-height: 95vh;
    margin: 0.5rem;
    width: calc(100% - 1rem);
    max-width: calc(100% - 1rem);
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 0.5rem;
    max-height: 98vh;
    margin: 0.25rem;
    width: calc(100% - 0.5rem);
    max-width: calc(100% - 0.5rem);
  }
`;

const MessageModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
    padding-bottom: 0.875rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }
`;

const MessageModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  flex: 1;
  min-width: 0;
  word-break: break-word;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    padding-right: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const MessageCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  transition: all 0.2s ease;

  &:hover {
    color: #111827;
    background: #f3f4f6;
  }

  &:active {
    transform: scale(0.95);
    background: #e5e7eb;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    min-width: 36px;
    min-height: 36px;
  }
`;

const MessageForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.25rem;

  @media (max-width: 768px) {
    gap: 0.875rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }

  /* Custom scrollbar for better mobile experience */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 44px;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }
`;

const FormSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  min-height: 44px;
  width: 100%;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  max-height: 300px;
  resize: vertical;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  overflow-y: auto;
  word-wrap: break-word;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 100px;
    max-height: 250px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 100px;
    max-height: 200px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
  flex-shrink: 0;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 0.75rem;
    margin-top: 0.75rem;
    padding-top: 0.875rem;
  }

  @media (max-width: 480px) {
    gap: 0.625rem;
    margin-top: 0.625rem;
    padding-top: 0.75rem;
  }
`;

const CancelButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: #e5e7eb;
  }

  &:active {
    transform: scale(0.98);
    background: #d1d5db;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }
`;

const EditButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 36px;
  z-index: 10;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: white;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 0.875rem;
    right: 0.875rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    min-height: 40px;
  }

  @media (max-width: 480px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.4375rem 0.75rem;
    font-size: 0.75rem;
    min-height: 36px;
  }
`;

const SendButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: #2563eb;
  }

  &:active {
    transform: scale(0.98);
    background: #1d4ed8;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }
`;

const RecentLogsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
`;

const LogItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const LogInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PainIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    if (props.level === 'worst-pain') return '#ef4444';
    if (props.level === 'severe') return '#f97316';
    if (props.level === 'moderate') return '#f59e0b';
    if (props.level === 'mild') return '#60a5fa';
    if (props.level === 'no-pain') return '#10b981';
    return '#e5e7eb';
  }};
`;

const LogDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LogPatient = styled.div`
  font-weight: 500;
  color: #374151;
`;

const LogZone = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const LogTime = styled.div`
  font-size: 0.875rem;
  color: #9ca3af;
`;

const LogLevel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => {
    if (props.level === 'worst-pain') return '#ef4444';
    if (props.level === 'severe') return '#f97316';
    if (props.level === 'moderate') return '#f59e0b';
    if (props.level === 'mild') return '#60a5fa';
    if (props.level === 'no-pain') return '#10b981';
    return '#6b7280';
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ProfileBanner = styled.div`
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    padding-top: 3.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    padding-top: 3rem;
  }
`;

const AvatarCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: white;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: 700;
  color: #059669;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
    font-size: 2rem;
  }
`;

const DoctorName = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const DoctorId = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  color: white;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9375rem;
  }

  @media (max-width: 480px) {
    padding: 0.375rem 0.875rem;
    font-size: 0.875rem;
  }
`;

const ProfileContent = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.3;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    margin-bottom: 0.75rem;
    gap: 0.375rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (max-width: 480px) {
    gap: 0.1875rem;
  }
`;

const InfoLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.4;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: #111827;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 2rem 0;

  @media (max-width: 768px) {
    margin: 1.5rem 0;
  }

  @media (max-width: 480px) {
    margin: 1.25rem 0;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  color: ${props => props.active ? '#059669' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  border-bottom: 2px solid ${props => props.active ? '#059669' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #059669;
  }
`;

const PatientTabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    gap: 0.375rem;
    margin-bottom: 1rem;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 480px) {
    gap: 0.25rem;
    margin-bottom: 0.875rem;
    padding-bottom: 2px;
  }
`;

const PatientTab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.active ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'transparent'};
  color: ${props => props.active ? '#ffffff' : '#6b7280'};
  font-weight: 700;
  font-size: 0.9375rem;
  border-radius: ${props => props.active ? '8px 8px 0 0' : '0'};
  border-bottom: 2px solid ${props => props.active ? '#047857' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  transform: ${props => props.active ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)'};
  box-shadow: ${props => props.active ? '0 4px 12px -2px rgba(5, 150, 105, 0.4)' : 'none'};
  z-index: ${props => props.active ? '2' : '1'};
  flex-shrink: 0;
  min-height: 44px;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: 768px) {
    padding: 0.75rem 1.125rem;
    font-size: 0.875rem;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
    min-height: 44px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    opacity: ${props => props.active ? '1' : '0'};
    transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    border-radius: ${props => props.active ? '8px 8px 0 0' : '0'};
    z-index: -1;
  }

  &:hover {
    color: ${props => props.active ? '#ffffff' : '#059669'};
    background: ${props => props.active 
      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' 
      : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
    transform: ${props => props.active ? 'translateY(-2px) scale(1.02)' : 'translateY(-1px) scale(1.01)'};
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  &:active {
    transform: ${props => props.active ? 'translateY(-1px) scale(1.01)' : 'translateY(0) scale(1)'};
    transition: all 0.15s ease-out;
  }
`;

const TabContentWrapper = styled.div`
  position: relative;
  min-height: 400px;
  max-height: calc(92vh - 13rem);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

const TabContent = styled.div`
  animation: fadeInSlide 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @keyframes fadeInSlide {
    from {
      opacity: 0;
      transform: translateY(15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PatientModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 1rem;
    z-index: 1001;
  }

  @media (max-width: 480px) {
    padding: 0.25rem;
    padding-top: 0.5rem;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 1200px;
  width: 92vw;
  min-height: 60vh;
  max-height: 92vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
    max-height: 95vh;
    margin: 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 8px;
    max-height: 98vh;
    margin: 0.25rem;
    padding-top: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 500;
  min-height: 44px;
  min-width: 44px;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: #dc2626;
  }

  &:active {
    transform: scale(0.95);
    background: #b91c1c;
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    min-height: 40px;
    min-width: 40px;
  }
`;


const CompactProfileCard = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  animation: slideDown 0.3s ease;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CompactBanner = styled.div`
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CompactAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #059669;
  flex-shrink: 0;
`;

const CompactInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompactName = styled.h3`
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactId = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
`;

const DoctorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const doctorUser = JSON.parse(localStorage.getItem('doctorUser') || '{}');
  const [doctorFeatures, setDoctorFeatures] = useState(doctorUser.features || {});
  const featureUser = { features: doctorFeatures };
  const canPrescribe = hasFeature(featureUser, 'medicationReminders');
  const canMessage = hasFeature(featureUser, 'messaging');
  const canSeeDistress = hasFeature(featureUser, 'distressThermometer');

  useEffect(() => {
    const token = localStorage.getItem('doctorToken');
    if (!token) return;
    axios.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const features = response.data?.user?.features;
        if (!features) return;
        setDoctorFeatures(features);
        const stored = JSON.parse(localStorage.getItem('doctorUser') || '{}');
        localStorage.setItem('doctorUser', JSON.stringify({ ...stored, features }));
      })
      .catch(() => {});
  }, []);
  
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalLogs: 0,
    worstPain: 0,
    severePain: 0,
    moderatePain: 0,
    mildPain: 0,
    noPain: 0
  });
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [sidebarFilteredPatients, setSidebarFilteredPatients] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [prioritizedPatients, setPrioritizedPatients] = useState([]);
  const [patientDistressData, setPatientDistressData] = useState({}); // patientId -> latest distress log
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState('');
  const [allPatientFilters, setAllPatientFilters] = useState(EMPTY_PATIENT_FILTERS);
  const [activeTab, setActiveTab] = useState('overview'); // Default to 'overview'
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientViewTab, setPatientViewTab] = useState('logs');
  const [dosesTaken, setDosesTaken] = useState([]);

  useEffect(() => {
    if (patientViewTab !== 'doses' || !selectedPatient?._id) return;
    const token = localStorage.getItem('doctorToken');
    axios.get(`/api/medication-doses/patient/${selectedPatient._id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((response) => {
      setDosesTaken(response.data.doses || []);
    }).catch(() => setDosesTaken([]));
  }, [patientViewTab, selectedPatient]);
  const [isProfileExpanded, setIsProfileExpanded] = useState(false);
  const [messagePatient, setMessagePatient] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    type: 'general'
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Medication-specific state
  const [medicationPatient, setMedicationPatient] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date()); // For Day to Day date selection
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());

  // Helper function to calculate patient age in real-time
  const getPatientAge = (patient) => {
    if (!patient?.dateOfBirth) return patient?.age || null;
    return calculateAge(patient.dateOfBirth);
  };

  // Auto-update age when month changes
  useEffect(() => {
    const checkMonthChange = () => {
      const now = new Date();
      const currentMonthNow = now.getMonth();
      if (currentMonthNow !== currentMonth) {
        setCurrentMonth(currentMonthNow);
        // Only update if we have patients - this prevents unnecessary re-renders
        // The age calculation happens in getPatientAge() which is called during render
        // No need to force a state update that triggers all dependent effects
      }
    };

    // Check every hour for month change (only when window is focused)
    const interval = setInterval(() => {
      if (document.hasFocus()) {
        checkMonthChange();
      }
    }, 60 * 60 * 1000);
    
    // Also check on mount
    checkMonthChange();

    return () => clearInterval(interval);
  }, [currentMonth]);

  // Load language preference on mount
  const canTranslate = hasFeature(featureUser, 'translation');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!canTranslate) {
      i18n.changeLanguage('en');
      return;
    }
    const stored = localStorage.getItem('preferredLanguage');
    if (stored) {
      i18n.changeLanguage(stored);
    }
  }, [canTranslate]);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile && sidebarOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      // Cleanup: restore scroll on unmount
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [sidebarOpen]);

  // Listen for closeSidebar event from FloatingAboutButton
  useEffect(() => {
    const handleCloseSidebar = (e) => {
      // Close sidebar immediately
      e?.preventDefault?.();
      setSidebarOpen(false);
    };

    window.addEventListener('closeSidebar', handleCloseSidebar);
    return () => {
      window.removeEventListener('closeSidebar', handleCloseSidebar);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('section') === 'profile') {
      setIsProfileExpanded(true);
      // Always close sidebar when navigating to profile section
      // This ensures profile content is visible, especially on mobile
      // Use setTimeout to ensure this happens after any other state updates
      setTimeout(() => {
        setSidebarOpen(false);
      }, 0);
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const getInitials = () => {
    const first = doctorUser?.firstName?.[0] || 'D';
    const last = doctorUser?.lastName?.[0] || 'R';
    return (first + last).toUpperCase();
  };

  const getFullName = () => {
    return `${doctorUser?.firstName || ''} ${doctorUser?.lastName || ''}`.trim() || 'Doctor';
  };

  const toggleProfile = () => {
    setIsProfileExpanded(!isProfileExpanded);
    if (isProfileExpanded && isEditingProfile) {
      setIsEditingProfile(false);
    }
    // Close sidebar when toggling profile (on all screen sizes for better UX)
    setSidebarOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (isProfileExpanded) {
      setIsProfileExpanded(false);
    }
    // Reset selected date to today when switching to Day to Day tab
    if (tab === 'dayToDay') {
      setSelectedDate(new Date());
    }
    // Close sidebar when tab is selected (on all screen sizes for better UX)
    setSidebarOpen(false);
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setProfileForm({
      firstName: doctorUser?.firstName || '',
      lastName: doctorUser?.lastName || '',
      email: doctorUser?.email || '',
      phone: doctorUser?.phone || ''
    });
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setProfileForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setUpdatingProfile(true);
      const token = localStorage.getItem('doctorToken') || JSON.parse(localStorage.getItem('doctorUser') || '{}').token;
      
      const response = await axios.put('/api/auth/profile', {
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        email: profileForm.email.trim(),
        phone: profileForm.phone.trim()
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.success('Profile updated successfully!');
        // Update local doctor user data
        const updatedUser = {
          ...doctorUser,
          ...response.data.user
        };
        localStorage.setItem('doctorUser', JSON.stringify({
          ...JSON.parse(localStorage.getItem('doctorUser') || '{}'),
          ...updatedUser
        }));
        setIsEditingProfile(false);
        // Refresh the page to get updated data
        window.location.reload();
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleOpenMessage = (patient) => {
    setMessagePatient(patient);
    setMessageForm({
      subject: '',
      message: '',
      type: 'general'
    });
  };

  const handleCloseMessage = () => {
    setMessagePatient(null);
    setMessageForm({
      subject: '',
      message: '',
      type: 'general'
    });
  };

  const handleOpenMedication = (patient) => {
    setMedicationPatient(patient);
  };

  const handleCloseMedication = () => {
    setMedicationPatient(null);
  };



  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messagePatient || !messageForm.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('doctorToken') || JSON.parse(localStorage.getItem('doctorUser') || '{}').token;
      
      const response = await axios.post('/api/messages', {
        patientId: messagePatient._id,
        subject: messageForm.subject.trim(),
        message: messageForm.message.trim(),
        type: messageForm.type
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.success('Message sent successfully!');
        handleCloseMessage();
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Use refs to track modal states without causing effect re-runs
  const medicationPatientRef = useRef(medicationPatient);
  const messagePatientRef = useRef(messagePatient);

  // Update refs when modal states change
  useEffect(() => {
    medicationPatientRef.current = medicationPatient;
  }, [medicationPatient]);

  useEffect(() => {
    messagePatientRef.current = messagePatient;
  }, [messagePatient]);

  // Initial data fetch - only on mount
  useEffect(() => {
    fetchDashboardData();
  }, []); // Empty dependency array - only run on mount

  // Set up polling - use refs so interval doesn't need to be recreated
  useEffect(() => {
    // Set up polling to refresh data every 3 minutes to catch new patient input
    // BUT: Skip polling if any modal is open to prevent form resets
    // Also only poll when window is focused to avoid unnecessary refreshes
    const interval = setInterval(() => {
      // Don't refresh if:
      // 1. Any modal is open (prevents form reset) - check refs for latest values
      // 2. Window is not focused (user is away)
      if (!medicationPatientRef.current && 
          !messagePatientRef.current && document.hasFocus()) {
        fetchDashboardData();
      }
    }, 180000); // 3 minutes (180000ms) - reduced from 30 seconds to minimize UI disruption
    
    return () => clearInterval(interval);
  }, []); // Empty dependency - interval created once and uses refs for current state


  // Calculate priority score based on pain scale
  const calculatePainPriorityScore = (patient, allLogs) => {
    if (!patient || !patient._id || !allLogs || allLogs.length === 0) {
      return 0;
    }

    const patientId = patient._id?.toString();
    if (!patientId) {
      console.warn(`Patient ${patient.patientId} has no _id`);
      return 0;
    }

    // Match logs to patient - handle both populated and non-populated userId
    const patientLogs = allLogs.filter(log => {
      if (!log || !log.userId) return false;
      
      let logUserId;
      if (typeof log.userId === 'object') {
        // Populated userId (from API)
        logUserId = log.userId._id || log.userId;
        // Handle case where _id might be an ObjectId
        if (logUserId && typeof logUserId === 'object' && logUserId.toString) {
          logUserId = logUserId.toString();
        }
      } else {
        // Direct userId string/ObjectId
        logUserId = log.userId;
      }
      
      const logId = logUserId?.toString();
      const matches = logId === patientId;
      
      return matches;
    });

    if (patientLogs.length === 0) {
      return 0;
    }

    // Recent logs (last 3 days) have higher weight
    const threeDaysAgo = subDays(new Date(), 3);
    const recentLogs = patientLogs.filter(log => {
      const logDate = log.timestamp ? new Date(log.timestamp) : (log.createdAt ? new Date(log.createdAt) : null);
      return logDate && logDate >= threeDaysAgo;
    });
    const olderLogs = patientLogs.filter(log => {
      const logDate = log.timestamp ? new Date(log.timestamp) : (log.createdAt ? new Date(log.createdAt) : null);
      return logDate && logDate < threeDaysAgo;
    });

    let score = 0;

    // Pain level weights (higher = more priority)
    const painLevelWeights = {
      'worst-pain': 12,
      'severe': 9,
      'moderate': 5,
      'mild': 2,
      'no-pain': 0
    };

    // Calculate score from recent logs (higher weight)
    recentLogs.forEach(log => {
      const levelWeight = painLevelWeights[log.painLevel] || 0;
      const intensityWeight = (log.painIntensity || 0) * 0.5;
      const recencyWeight = 2; // Recent logs get 2x weight
      score += (levelWeight + intensityWeight) * recencyWeight;
    });

    // Calculate score from older logs (lower weight)
    olderLogs.forEach(log => {
      const levelWeight = painLevelWeights[log.painLevel] || 0;
      const intensityWeight = (log.painIntensity || 0) * 0.3;
      score += levelWeight + intensityWeight;
    });

    // Frequency bonus: more frequent pain reports = higher priority
    const frequencyBonus = patientLogs.length * 0.5;
    score += frequencyBonus;

    // Recent severe pain gets extra boost
    const recentSevere = recentLogs.filter(log => log.painLevel === 'worst-pain' || log.painLevel === 'severe').length;
    score += recentSevere * 15;

    return Math.max(0, score); // Ensure non-negative
  };

  // Calculate priority score based on distress thermometer
  const calculateDistressPriorityScore = (patient, distressData) => {
    if (!patient || !patient._id || !distressData) {
      return 0;
    }

    const patientDistress = distressData[patient._id];
    if (!patientDistress || !patientDistress.distressLog) {
      return 0;
    }

    const distressLog = patientDistress.distressLog;
    const distressScore = distressLog.distressScore;

    if (typeof distressScore !== 'number' || distressScore < 0 || distressScore > 10) {
      return 0;
    }

    // Calculate score based on distress level
    // Higher distress = higher priority
    // Scale: 0-2 (low) = 0-5, 3-5 (moderate) = 6-15, 6-8 (high) = 16-30, 9-10 (severe) = 31-50
    let score = 0;
    
    if (distressScore >= 9) {
      score = 31 + (distressScore - 9) * 19; // 31-50 for 9-10
    } else if (distressScore >= 6) {
      score = 16 + (distressScore - 6) * 4.67; // 16-30 for 6-8
    } else if (distressScore >= 3) {
      score = 6 + (distressScore - 3) * 3; // 6-15 for 3-5
    } else {
      score = distressScore * 2.5; // 0-5 for 0-2
    }

    // Recent distress gets higher weight (check if log is recent)
    const logDate = distressLog.timestamp ? new Date(distressLog.timestamp) : (distressLog.createdAt ? new Date(distressLog.createdAt) : null);
    if (logDate) {
      const threeDaysAgo = subDays(new Date(), 3);
      if (logDate >= threeDaysAgo) {
        score *= 1.5; // 50% boost for recent distress
      }
    }

    return Math.max(0, Math.round(score)); // Ensure non-negative and round
  };

  // Fetch and calculate prioritized patients
  useEffect(() => {
    const calculatePriorities = async () => {
      // If no patients, set empty array
      if (patients.length === 0) {
        setPrioritizedPatients([]);
        return;
      }

      // Don't block on loading state - calculate with available data
      // The calculation will update when new data arrives
      try {
        // Use the priority scores already calculated in patients state (from fetchDashboardData)
        // This ensures we're using the most recent calculation
        const patientsWithPriority = patients
          .map(patient => {
            // Calculate pain-based priority score
            const painPriorityScore = patient.painPriorityScore !== undefined 
              ? patient.painPriorityScore 
              : calculatePainPriorityScore(patient, recentLogs);
            
            // Calculate distress-based priority score
            const distressPriorityScore = patient.distressPriorityScore !== undefined
              ? patient.distressPriorityScore
              : calculateDistressPriorityScore(patient, patientDistressData);
            
            // Get latest pain log for display
            const patientLogs = recentLogs.filter(log => {
              if (!log || !log.userId) return false;
              let logUserId = typeof log.userId === 'object' ? log.userId._id || log.userId : log.userId;
              return logUserId?.toString() === patient._id?.toString();
            });
            
            const latestLog = patientLogs.length > 0 
              ? patientLogs.sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))[0]
              : null;

            // Check if patient has distress data
            const hasDistressData = patientDistressData[patient._id] !== undefined && patientDistressData[patient._id] !== null;

            return {
              ...patient,
              painPriorityScore: Math.max(0, painPriorityScore), // Ensure non-negative
              distressPriorityScore: Math.max(0, distressPriorityScore), // Ensure non-negative
              latestLog,
              painLogsCount: patientLogs.length,
              hasDistressData
            };
          })
          .filter(patient => {
            // Only include patients who have at least one priority score > 0
            const hasPriority = patient.painPriorityScore > 0 || patient.distressPriorityScore > 0;
            
            return hasPriority;
          });

        // Sort by highest priority score (pain or distress), then by patient name
        const sorted = patientsWithPriority.sort((a, b) => {
          const maxA = Math.max(a.painPriorityScore || 0, a.distressPriorityScore || 0);
          const maxB = Math.max(b.painPriorityScore || 0, b.distressPriorityScore || 0);
          if (maxB !== maxA) {
            return maxB - maxA;
          }
          return (a.fullName || '').localeCompare(b.fullName || '');
        });
        
        setPrioritizedPatients(sorted);
      } catch (error) {
        console.error('Error calculating priorities:', error);
        console.error('Error stack:', error.stack);
        setPrioritizedPatients([]);
      }
    };

    // Always calculate priorities (not just when priority tab is active) so badge count is accurate
    calculatePriorities();
  }, [patients, recentLogs, patientDistressData, loading]);

  // Check if patient is new (registered within last 7 days)
  const isNewPatient = (patient) => {
    if (!patient.registeredDate && !patient.createdAt) return false;
    const registeredDate = new Date(patient.registeredDate || patient.createdAt);
    const sevenDaysAgo = subDays(new Date(), 7);
    return registeredDate >= sevenDaysAgo;
  };

  // Check if patient was registered on a specific date (for Day to Day filter)
  const isDatePatient = (patient, date) => {
    if (!patient.registeredDate && !patient.createdAt) return false;
    const registeredDate = new Date(patient.registeredDate || patient.createdAt);
    const dateStart = startOfDay(date);
    const dateEnd = endOfDay(date);
    return registeredDate >= dateStart && registeredDate <= dateEnd;
  };

  // Check if patient was registered today (for Day to Day filter - backward compatibility)
  const isTodayPatient = (patient) => {
    return isDatePatient(patient, selectedDate);
  };


  // Get patient counts
  const todayPatientsCount = patients.filter(p => isDatePatient(p, selectedDate)).length;
  const allPatientsCount = patients.length;

  const matchesPatientStatus = (patient, status) => {
    switch (status) {
      case 'active': return patient.isActive !== false;
      case 'inactive': return patient.isActive === false;
      case 'new': return isNewPatient(patient);
      case 'attention': return (patient.painPriorityScore || 0) > 0 || (patient.distressPriorityScore || 0) > 0;
      default: return true;
    }
  };

  const nameFilter = allPatientFilters.name.trim().toLowerCase();
  const idFilter = allPatientFilters.id.trim().toLowerCase();
  const idFilterDigits = idFilter.replace(/\D/g, '');
  const allPatientsFiltered = patients.filter((patient) => {
    const fullName = (patient.fullName || `${patient.firstName || ''} ${patient.lastName || ''}`).toLowerCase();
    if (nameFilter && !fullName.includes(nameFilter)) return false;
    if (idFilter) {
      const idMatch = String(patient.patientId || '').toLowerCase().includes(idFilter);
      const phoneMatch = idFilterDigits.length >= 3 && String(patient.phone || '').replace(/\D/g, '').includes(idFilterDigits);
      if (!idMatch && !phoneMatch) return false;
    }
    return matchesPatientStatus(patient, allPatientFilters.status);
  });
  const allPatientFiltersActive = Boolean(nameFilter || idFilter || allPatientFilters.status !== 'all');
  const updateAllPatientFilter = (key) => (event) => {
    const { value } = event.target;
    setAllPatientFilters((current) => ({ ...current, [key]: value }));
  };

  // Get patients based on active tab
  const getPatientsForTab = (patientList) => {
    if (activeTab === 'dayToDay') {
      return patientList.filter(p => isDatePatient(p, selectedDate));
    }
    return patientList; // 'allPatients' or 'overview'
  };

  useEffect(() => {
    const tabPatients = getPatientsForTab(patients);
    if (searchTerm) {
      const filtered = tabPatients.filter(patient => 
        patient.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.patientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patient.phone && patient.phone.includes(searchTerm))
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(tabPatients);
    }
  }, [searchTerm, patients, activeTab, selectedDate]);

  useEffect(() => {
    const tabPatients = getPatientsForTab(patients);
    if (sidebarSearchTerm) {
      const filtered = tabPatients.filter(patient => 
        patient.fullName.toLowerCase().includes(sidebarSearchTerm.toLowerCase()) ||
        patient.patientId.toLowerCase().includes(sidebarSearchTerm.toLowerCase()) ||
        (patient.phone && patient.phone.includes(sidebarSearchTerm))
      );
      setSidebarFilteredPatients(filtered);
    } else {
      setSidebarFilteredPatients(tabPatients);
    }
  }, [sidebarSearchTerm, patients, activeTab, selectedDate]);

  const handlePatientSelect = (patient) => {
    setSelectedPatientId(patient._id || patient.patientId);
    setSelectedPatient(patient);
    handleViewPatient(patient);
    // Close sidebar after selection (on all screen sizes for better UX)
    setSidebarOpen(false);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Set auth token for doctor - try multiple sources
      let token = localStorage.getItem('doctorToken') || 
                  localStorage.getItem('token');
      
      // Try to get from doctorUser object if not found
      if (!token) {
        try {
          const doctorUser = localStorage.getItem('doctorUser');
          if (doctorUser) {
            const parsed = JSON.parse(doctorUser);
            token = parsed.token;
          }
        } catch (e) {
          console.warn('Could not parse doctorUser from localStorage');
        }
      }
      
      if (!token) {
        const error = new Error('No authentication token found');
        error.isRefreshError = true;
        throw error;
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch all patients
      const patientsResponse = await axios.get('/api/auth/patients', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const patientsData = patientsResponse.data.patients || [];
      
      if (patientsData.length === 0) {
        console.warn('No patients found! Check if patients are assigned to this doctor.');
      }
      
      setStats(prev => ({ ...prev, totalPatients: patientsData.length }));

      // Fetch all recent pain logs (last 30 days to ensure we capture all relevant logs)
      const endDate = new Date();
      const startDate = subDays(endDate, 30);
      
      let logs = [];
      try {
        const logsResponse = await axios.get('/api/pain-logs/all', {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit: 500 // Increase limit to get more logs
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        logs = logsResponse.data.painLogs || [];
      } catch (error) {
        console.error('Error fetching pain logs:', error.response?.data || error.message);
        // Continue with empty logs array - priority will be 0 for all patients
        logs = [];
      }
      
      setRecentLogs(logs);

      // Calculate priority scores for all patients
      const patientsWithPriority = patientsData.map(patient => {
        // Improved userId matching with better debugging
        const patientLogs = logs.filter(log => {
          if (!log || !log.userId) return false;
          
          let logUserId;
          if (typeof log.userId === 'object') {
            // Populated userId (from API)
            logUserId = log.userId._id || log.userId;
          } else {
            // Direct userId string/ObjectId
            logUserId = log.userId;
          }
          
          const patientId = patient._id?.toString();
          const logId = logUserId?.toString();
          
          const matches = logId === patientId;
          return matches;
        });
        
        const painPriorityScore = calculatePainPriorityScore(patient, logs);
        const distressPriorityScore = calculateDistressPriorityScore(patient, {});
        
        return {
          ...patient,
          painPriorityScore: Math.max(0, painPriorityScore), // Ensure non-negative
          distressPriorityScore: Math.max(0, distressPriorityScore) // Ensure non-negative
        };
      });

      setPatients(patientsWithPriority);

      // Fetch distress logs for all patients (in parallel for better performance)
      const distressPatients = canSeeDistress ? patientsWithPriority : [];
      const distressPromises = distressPatients.map(async (patient) => {
        try {
          // Use the same token we used for other requests
          const distressResponse = await axios.get('/api/distress-logs/patient/' + patient._id, {
            params: {
              limit: 1 // Get only the latest distress log
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          if (distressResponse.data.success && distressResponse.data.distressLogs) {
            const distressLogs = distressResponse.data.distressLogs || [];
            return distressLogs.length > 0 ? { patientId: patient._id, distressLog: distressLogs[0] } : null;
          }
          return null;
        } catch (error) {
          console.error(`Error fetching distress for patient ${patient._id}:`, error.response?.data || error.message);
          return null;
        }
      });

      const distressResults = await Promise.all(distressPromises);
      const distressDataMap = {};
      distressResults.forEach(result => {
        if (result) {
          distressDataMap[result.patientId] = { distressLog: result.distressLog };
        }
      });
      setPatientDistressData(distressDataMap);

      // Recalculate distress priority scores with actual distress data
      const patientsWithDistressScores = patientsWithPriority.map(patient => {
        const distressPriorityScore = calculateDistressPriorityScore(patient, distressDataMap);
        return {
          ...patient,
          distressPriorityScore: Math.max(0, distressPriorityScore)
        };
      });
      setPatients(patientsWithDistressScores);

      // Calculate statistics
      const worstPain = logs.filter(log => log.painLevel === 'worst-pain').length;
      const severePain = logs.filter(log => log.painLevel === 'severe').length;
      const moderatePain = logs.filter(log => log.painLevel === 'moderate').length;
      const mildPain = logs.filter(log => log.painLevel === 'mild').length;
      const noPain = logs.filter(log => log.painLevel === 'no-pain').length;

      setStats(prev => ({
        ...prev,
        totalLogs: logs.length,
        worstPain,
        severePain,
        moderatePain,
        mildPain,
        noPain
      }));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        
        // Handle specific error cases
        if (error.response.status === 401) {
          toast.error('Authentication failed. Please login again.');
          return;
        }
        if (error.response.status === 403) {
          toast.error('Access denied. Please check your permissions.');
          return;
        }
      }
      
      if (!error.isRefreshError) {
        toast.error('Failed to load dashboard data. Please refresh the page.');
      }
      throw error; // Re-throw so caller can handle it
    } finally {
      setLoading(false);
    }
  };

  const handleViewPatient = async (patient) => {
    if (!patient?.patientId) {
      console.error('Patient missing patientId:', patient);
      toast.error('Error: Patient ID not found. Please refresh and try again.');
      return;
    }
    setSelectedPatient(patient);
    setPatientViewTab('logs');
  };

  const handleLogout = () => {
    // Set a flag to indicate logout is in progress (prevents showing doctor login)
    localStorage.setItem('doctorLoggingOut', 'true');
    
    localStorage.removeItem('doctorToken');
    localStorage.removeItem('doctorRefreshToken');
    localStorage.removeItem('doctorUser');
    localStorage.removeItem('doctorReferrer');
    delete axios.defaults.headers.common['Authorization'];
    
    // Dispatch custom event to notify DoctorProtectedRoute
    window.dispatchEvent(new Event('doctorLogout'));
    
    toast.success('Logged out successfully');
    
    // Redirect immediately to prevent showing doctor login page
    window.location.href = '/login';
  };

  const getPainLevelLabel = (level) => {
    switch(level) {
      case 'worst-pain': return t('dashboard.stats.worstPain');
      case 'severe': return t('dashboard.stats.severePain');
      case 'moderate': return t('dashboard.stats.moderatePain');
      case 'mild': return t('dashboard.stats.mild');
      case 'no-pain': return t('dashboard.stats.noPain');
      default: return t('dashboard.stats.unknown');
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingSpinner text="Loading doctor dashboard..." />
      </DashboardContainer>
    );
  }

  // Get sorted sidebar patients based on active tab
  const sortedSidebarPatients = (() => {
    const patientsToSort = sidebarSearchTerm 
      ? sidebarFilteredPatients 
      : getPatientsForTab(patients);
    
    return [...patientsToSort].sort((a, b) => {
      const aIsNew = isNewPatient(a);
      const bIsNew = isNewPatient(b);
      
      if (aIsNew && !bIsNew) return -1;
      if (!aIsNew && bIsNew) return 1;
      
      return (a.fullName || '').localeCompare(b.fullName || '');
    });
  })();

  const newPatientsCount = patients.filter(p => isNewPatient(p)).length;

  return (
    <DashboardContainer>
      <MobileMenuButton 
        onClick={(e) => {
          e.stopPropagation();
          setSidebarOpen(!sidebarOpen);
        }}
      >
        {sidebarOpen ? '✕' : '☰'}
      </MobileMenuButton>
      <Overlay 
        show={sidebarOpen} 
        onClick={(e) => {
          e.stopPropagation();
          setSidebarOpen(false);
        }}
      />
      
      <Sidebar 
        isOpen={sidebarOpen}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', flexShrink: 0 }}>
          <img src="/logo-wordmark.jpg" alt="Anuvedana" style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 12 }} />
          <HospitalLogo logoUrl={doctorUser.hospitalLogoUrl} name={doctorUser.hospitalName} size={48} stacked style={{ marginTop: '0.4rem' }} />
        </div>
        <SidebarTabsContainer>
          <SidebarTab 
            active={activeTab === 'overview'} 
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange('overview');
            }}
          >
            <span>📊 Overview</span>
          </SidebarTab>
          <SidebarTab 
            active={activeTab === 'priority'} 
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange('priority');
            }}
          >
            <span>🔴 Priority</span>
            <TabBadge active={activeTab === 'priority'}>
              {prioritizedPatients.length}
            </TabBadge>
          </SidebarTab>
          <SidebarTab 
            active={activeTab === 'dayToDay'} 
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange('dayToDay');
            }}
          >
            <span>📅 Day to Day</span>
            <TabBadge active={activeTab === 'dayToDay'}>{todayPatientsCount}</TabBadge>
          </SidebarTab>
          <SidebarTab 
            active={activeTab === 'allPatients'} 
            onClick={(e) => {
              e.stopPropagation();
              handleTabChange('allPatients');
            }}
          >
            <span>👥 All Patients</span>
            <TabBadge active={activeTab === 'allPatients'}>{allPatientsCount}</TabBadge>
          </SidebarTab>
        </SidebarTabsContainer>
        
        <SidebarContent>
          {/* Patient cards are displayed in the main content area */}
        </SidebarContent>
        
        <UserSection>
          <UserProfile 
            onClick={(e) => {
              e.stopPropagation();
              toggleProfile();
            }}
          >
            <UserAvatar>{getInitials()}</UserAvatar>
            <UserInfo>
              <UserName>Dr. {getFullName()}</UserName>
              <UserLabel>Doctor Profile</UserLabel>
            </UserInfo>
          </UserProfile>
          <LogoutButton 
            onClick={(e) => {
              e.stopPropagation();
              handleLogout();
            }}
          >
            🚪 Logout
          </LogoutButton>
        </UserSection>
      </Sidebar>

      <MainContent>
        <Header>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, lineHeight: '1.3' }}>Doctor Dashboard</h1>
          {!isProfileExpanded && activeTab === 'overview' && (
            <WelcomeMessage>
              Welcome, Dr. {doctorUser?.firstName || 'Doctor'}!
            </WelcomeMessage>
          )}
        </Header>

      {isProfileExpanded ? (
        <>
          <ProfileCard>
            <ProfileBanner>
              <EditButton onClick={handleEditProfile}>
                ✏️ Edit Profile
              </EditButton>
              <AvatarCircle>{getInitials()}</AvatarCircle>
              <DoctorName>Dr. {getFullName()}</DoctorName>
              <DoctorId>🆔 {doctorUser?.doctorId || 'N/A'}</DoctorId>
            </ProfileBanner>

            <ProfileContent>
              {isEditingProfile ? (
                <MessageForm onSubmit={handleUpdateProfile}>
                  <Section>
                    <SectionTitle>
                      ✏️ Edit Personal Information
                    </SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>First Name *</InfoLabel>
                        <FormInput
                          type="text"
                          value={profileForm.firstName}
                          onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                          required
                          maxLength={50}
                          placeholder="First Name"
                        />
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Last Name</InfoLabel>
                        <FormInput
                          type="text"
                          value={profileForm.lastName}
                          onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                          maxLength={50}
                          placeholder="Last Name"
                        />
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Doctor ID</InfoLabel>
                        <InfoValue style={{ opacity: 0.7 }}>{doctorUser?.doctorId || 'N/A'} (Cannot be changed)</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </Section>

                  <Divider />

                  <Section>
                    <SectionTitle>
                      📞 Edit Contact Information
                    </SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Email *</InfoLabel>
                        <FormInput
                          type="email"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          required
                          placeholder="Email address"
                        />
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Phone Number *</InfoLabel>
                        <FormInput
                          type="tel"
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                          required
                          placeholder="Phone number"
                        />
                      </InfoItem>
                    </InfoGrid>
                  </Section>

                  <FormActions style={{ marginTop: '2rem' }}>
                    <CancelButton type="button" onClick={handleCancelEdit}>
                      Cancel
                    </CancelButton>
                    <SendButton type="submit" disabled={updatingProfile || !profileForm.firstName.trim() || !profileForm.email.trim() || !profileForm.phone.trim()}>
                      {updatingProfile ? 'Updating...' : 'Save Changes'}
                    </SendButton>
                  </FormActions>
                </MessageForm>
              ) : (
                <>
                  <Section>
                    <SectionTitle>
                      👤 Personal Information
                    </SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>First Name</InfoLabel>
                        <InfoValue>{doctorUser?.firstName || 'N/A'}</InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Last Name</InfoLabel>
                        <InfoValue>{doctorUser?.lastName || 'Not provided'}</InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Doctor ID</InfoLabel>
                        <InfoValue>{doctorUser?.doctorId || 'N/A'}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </Section>

                  <Divider />

                  <Section>
                    <SectionTitle>
                      📞 Contact Information
                    </SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Email</InfoLabel>
                        <InfoValue>{doctorUser?.email || 'N/A'}</InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Phone Number</InfoLabel>
                        <InfoValue>{doctorUser?.phone || 'N/A'}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </Section>
                </>
              )}

              <Divider />

              <Section>
                <SectionTitle>
                  📊 Account Information
                </SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Account Status</InfoLabel>
                    <InfoValue style={{ color: '#10b981' }}>
                      ✓ Active
                    </InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel>Member Since</InfoLabel>
                    <InfoValue>
                      {doctorUser?.createdAt 
                        ? new Date(doctorUser.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </InfoValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel>Role</InfoLabel>
                    <InfoValue style={{ textTransform: 'capitalize' }}>Doctor</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </Section>
            </ProfileContent>
          </ProfileCard>

          <SectionCard style={{ textAlign: 'center', padding: '3rem', marginTop: '2rem' }}>
            <SectionTitle style={{ justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
              👥 Total Patients Handled
            </SectionTitle>
            <div style={{ fontSize: '4rem', fontWeight: 700, color: '#059669', marginBottom: '1rem', lineHeight: '1' }}>
              {stats.totalPatients}
            </div>
            <div style={{ fontSize: '1.125rem', color: '#6b7280', fontWeight: 500 }}>
              patient{stats.totalPatients !== 1 ? 's' : ''} assigned to you
            </div>
          </SectionCard>
        </>
      ) : (
        <>
          {activeTab === 'overview' && (
            <>
              <StatsGrid>
                <StatCard>
                  <StatIcon color="#059669">👥</StatIcon>
                  <StatValue>{stats.totalPatients}</StatValue>
                  <StatLabel>Total Patients</StatLabel>
                </StatCard>
                
                <StatCard>
                  <StatIcon color="#3b82f6">📅</StatIcon>
                  <StatValue>{todayPatientsCount}</StatValue>
                  <StatLabel>New Today</StatLabel>
                </StatCard>
                
                <StatCard>
                  <StatIcon color="#3b82f6">📊</StatIcon>
                  <StatValue>{stats.totalLogs}</StatValue>
                  <StatLabel>Total Logs (7 days)</StatLabel>
                </StatCard>
                
                <StatCard>
                  <StatIcon color="#ef4444">🔴</StatIcon>
                  <StatValue>{stats.worstPain}</StatValue>
                  <StatLabel>{t('dashboard.stats.worstPain')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatIcon color="#f97316">🟠</StatIcon>
                  <StatValue>{stats.severePain}</StatValue>
                  <StatLabel>{t('dashboard.stats.severePain')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatIcon color="#f59e0b">🟡</StatIcon>
                  <StatValue>{stats.moderatePain}</StatValue>
                  <StatLabel>{t('dashboard.stats.moderatePain')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatIcon color="#60a5fa">🔵</StatIcon>
                  <StatValue>{stats.mildPain}</StatValue>
                  <StatLabel>{t('dashboard.stats.mild')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatIcon color="#10b981">🟢</StatIcon>
                  <StatValue>{stats.noPain}</StatValue>
                  <StatLabel>{t('dashboard.stats.noPain')}</StatLabel>
                </StatCard>
              </StatsGrid>
            </>
          )}

          {activeTab === 'dayToDay' && (
            <SectionCard>
              <SectionTitle>
                📅 Day to Day Patients ({todayPatientsCount})
              </SectionTitle>
              <DateSelectorContainer>
                <DateLabel>
                  📅 Select Date:
                </DateLabel>
                <DateNavButton
                  onClick={() => {
                    const prevDate = new Date(selectedDate);
                    prevDate.setDate(prevDate.getDate() - 1);
                    setSelectedDate(prevDate);
                  }}
                  title="Previous Day"
                >
                  ← Previous
                </DateNavButton>
                <DateInput
                  type="date"
                  value={format(selectedDate, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setSelectedDate(newDate);
                  }}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
                <DateNavButton
                  onClick={() => {
                    const nextDate = new Date(selectedDate);
                    nextDate.setDate(nextDate.getDate() + 1);
                    const today = new Date();
                    if (nextDate <= today) {
                      setSelectedDate(nextDate);
                    }
                  }}
                  disabled={format(selectedDate, 'yyyy-MM-dd') >= format(new Date(), 'yyyy-MM-dd')}
                  title="Next Day"
                >
                  Next →
                </DateNavButton>
                <DateNavButton
                  onClick={() => setSelectedDate(new Date())}
                  style={{ background: '#3b82f6' }}
                  title="Go to Today"
                >
                  Today
                </DateNavButton>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: 'auto' }}>
                  Showing: {format(selectedDate, 'MMM dd, yyyy')}
                  {format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') && (
                    <span style={{ color: '#059669', fontWeight: 500 }}> (Today)</span>
                  )}
                </div>
              </DateSelectorContainer>
              <SearchInput
                type="text"
                placeholder="Search by name, Patient ID, or phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {filteredPatients.length > 0 ? (
                <PatientList>
                  {filteredPatients.map((patient) => {
                    const isNew = isNewPatient(patient);
                    const initials = `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase() || 'P';
                    return (
                      <PatientCard key={patient._id || patient.patientId}>
                        <PatientCardHeader>
                          <PatientAvatar>{initials}</PatientAvatar>
                          <div style={{ flex: 1 }}>
                            <PatientName>
                              {patient.fullName}
                              {isNew && <NewPatientIndicator>NEW</NewPatientIndicator>}
                            </PatientName>
                          </div>
                        </PatientCardHeader>
                        <PatientInfo>
                          <PatientDetails>
                            <PatientDetailItem>
                              <strong>ID:</strong> {patient.patientId}
                            </PatientDetailItem>
                            {patient.hospitalId && (
                              <PatientDetailItem>
                                <strong>Hospital ID:</strong> {patient.hospitalId}
                              </PatientDetailItem>
                            )}
                            <PatientDetailItem style={{ color: '#3b82f6', fontWeight: 500 }}>
                              📅 Registered: {format(new Date(patient.registeredDate || patient.createdAt), 'MMM dd, yyyy')}
                            </PatientDetailItem>
                            {(patient.painPriorityScore > 0 || patient.distressPriorityScore > 0) && (
                              <>
                                {patient.painPriorityScore > 0 && (
                                  <PatientDetailItem style={{ color: '#059669', fontWeight: 600 }}>
                                    <strong>Pain Priority Score:</strong> {Math.round(patient.painPriorityScore)}
                                  </PatientDetailItem>
                                )}
                                {patient.distressPriorityScore > 0 && (
                                  <PatientDetailItem style={{ color: '#f59e0b', fontWeight: 600 }}>
                                    <strong>Distress Priority Score:</strong> {Math.round(patient.distressPriorityScore)}
                                  </PatientDetailItem>
                                )}
                              </>
                            )}
                          </PatientDetails>
                        </PatientInfo>
                        <PatientActions>
                          {canPrescribe && (
                            <MedicationButton onClick={() => handleOpenMedication(patient)}>
                              💊 Medication
                            </MedicationButton>
                          )}
                          {canMessage && (
                            <MessageButton onClick={() => handleOpenMessage(patient)}>
                              💬 Message
                            </MessageButton>
                          )}
                          <ViewButton onClick={() => handleViewPatient(patient)}>
                            📋 View Logs
                          </ViewButton>
                        </PatientActions>
                      </PatientCard>
                    );
                  })}
                </PatientList>
              ) : (
                <EmptyState>
                  <EmptyIcon>👥</EmptyIcon>
                  <EmptyTitle>No patients found</EmptyTitle>
                  <EmptyText>
                    {searchTerm ? 'No patients match your search criteria' : 'No patients registered yet'}
                  </EmptyText>
                </EmptyState>
              )}
            </SectionCard>
          )}

          {activeTab === 'allPatients' && (
            <SectionCard>
              <SectionTitle>
                👥 All Patients ({allPatientsCount})
              </SectionTitle>
              <FilterBar role="search" aria-label="Filter patients">
                <FilterField>
                  Name
                  <input
                    type="text"
                    placeholder="Search by name"
                    value={allPatientFilters.name}
                    onChange={updateAllPatientFilter('name')}
                  />
                </FilterField>
                <FilterField>
                  Patient ID or phone
                  <input
                    type="text"
                    placeholder="e.g. DEMOP00001"
                    value={allPatientFilters.id}
                    onChange={updateAllPatientFilter('id')}
                  />
                </FilterField>
                <FilterField>
                  Status
                  <select value={allPatientFilters.status} onChange={updateAllPatientFilter('status')}>
                    <option value="all">All patients</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="new">New this week</option>
                    <option value="attention">Needs attention</option>
                  </select>
                </FilterField>
                <ClearFiltersButton
                  type="button"
                  onClick={() => setAllPatientFilters(EMPTY_PATIENT_FILTERS)}
                  disabled={!allPatientFiltersActive}
                >
                  Clear filters
                </ClearFiltersButton>
              </FilterBar>
              <FilterSummary role="status">
                Showing {allPatientsFiltered.length} of {allPatientsCount} patients
              </FilterSummary>
              {allPatientsFiltered.length > 0 ? (
                <PatientList>
                  {allPatientsFiltered.map((patient) => {
                    const isNew = isNewPatient(patient);
                    const initials = `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase() || 'P';
                    return (
                      <PatientCard key={patient._id || patient.patientId}>
                        <PatientCardHeader>
                          <PatientAvatar>{initials}</PatientAvatar>
                          <div style={{ flex: 1 }}>
                            <PatientName>
                              {patient.fullName}
                              {isNew && <NewPatientIndicator>NEW</NewPatientIndicator>}
                              {patient.isActive === false && <InactivePatientIndicator>INACTIVE</InactivePatientIndicator>}
                            </PatientName>
                          </div>
                        </PatientCardHeader>
                        <PatientInfo>
                          <PatientDetails>
                            <PatientDetailItem>
                              <strong>ID:</strong> {patient.patientId}
                            </PatientDetailItem>
                            {patient.hospitalId && (
                              <PatientDetailItem>
                                <strong>Hospital ID:</strong> {patient.hospitalId}
                              </PatientDetailItem>
                            )}
                            <PatientDetailItem style={{ color: '#3b82f6', fontWeight: 500 }}>
                              📅 Registered: {format(new Date(patient.registeredDate || patient.createdAt), 'MMM dd, yyyy')}
                            </PatientDetailItem>
                            {(patient.painPriorityScore > 0 || patient.distressPriorityScore > 0) && (
                              <>
                                {patient.painPriorityScore > 0 && (
                                  <PatientDetailItem style={{ color: '#059669', fontWeight: 600 }}>
                                    <strong>Pain Priority Score:</strong> {Math.round(patient.painPriorityScore)}
                                  </PatientDetailItem>
                                )}
                                {patient.distressPriorityScore > 0 && (
                                  <PatientDetailItem style={{ color: '#f59e0b', fontWeight: 600 }}>
                                    <strong>Distress Priority Score:</strong> {Math.round(patient.distressPriorityScore)}
                                  </PatientDetailItem>
                                )}
                              </>
                            )}
                          </PatientDetails>
                        </PatientInfo>
                        <PatientActions>
                          {canPrescribe && (
                            <MedicationButton onClick={() => handleOpenMedication(patient)}>
                              💊 Medication
                            </MedicationButton>
                          )}
                          {canMessage && (
                            <MessageButton onClick={() => handleOpenMessage(patient)}>
                              💬 Message
                            </MessageButton>
                          )}
                          <ViewButton onClick={() => handleViewPatient(patient)}>
                            📋 View Logs
                          </ViewButton>
                        </PatientActions>
                      </PatientCard>
                    );
                  })}
                </PatientList>
              ) : (
                <EmptyState>
                  <EmptyIcon>👥</EmptyIcon>
                  <EmptyTitle>No patients found</EmptyTitle>
                  <EmptyText>
                    {allPatientFiltersActive ? 'No patients match these filters' : 'No patients registered yet'}
                  </EmptyText>
                </EmptyState>
              )}
            </SectionCard>
          )}

          {activeTab === 'priority' && (
            <SectionCard>
              <SectionTitle>
                🔴 Priority Patients ({prioritizedPatients.length})
              </SectionTitle>
              <SearchInput
                type="text"
                placeholder="Search by name, Patient ID, or phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {prioritizedPatients.length > 0 ? (
                <PatientList>
                  {prioritizedPatients
                    .filter(patient => {
                      if (!searchTerm) return true;
                      return (
                        patient.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        patient.patientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (patient.phone && patient.phone.includes(searchTerm))
                      );
                    })
                    .map((patient) => {
                      const isNew = isNewPatient(patient);
                      const initials = `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase() || 'P';
                      const maxPriority = Math.max(patient.painPriorityScore || 0, patient.distressPriorityScore || 0);
                      const priorityLevel = maxPriority > 30 ? 'high' : maxPriority > 15 ? 'medium' : 'low';
                      return (
                        <PatientCard 
                          key={patient._id || patient.patientId}
                          style={{
                            borderLeft: priorityLevel === 'high' ? '4px solid #ef4444' : 
                                       priorityLevel === 'medium' ? '4px solid #f59e0b' : 
                                       '1px solid #e5e7eb'
                          }}
                        >
                          <PatientCardHeader>
                            <PatientAvatar>{initials}</PatientAvatar>
                            <div style={{ flex: 1 }}>
                              <PatientName>
                                {patient.fullName}
                                {isNew && <NewPatientIndicator>NEW</NewPatientIndicator>}
                                {patient.priorityScore > 0 && (
                                  <span style={{
                                    marginLeft: '0.5rem',
                                    padding: '0.25rem 0.5rem',
                                    background: priorityLevel === 'high' ? '#fee2e2' : 
                                               priorityLevel === 'medium' ? '#fef3c7' : '#f0fdf4',
                                    color: priorityLevel === 'high' ? '#991b1b' : 
                                          priorityLevel === 'medium' ? '#92400e' : '#065f46',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                  }}>
                                    {priorityLevel === 'high' ? '🔴 HIGH' : priorityLevel === 'medium' ? '🟡 MEDIUM' : '🟢 LOW'}
                                  </span>
                                )}
                              </PatientName>
                            </div>
                          </PatientCardHeader>
                          <PatientInfo>
                            <PatientDetails>
                              <PatientDetailItem>
                                <strong>ID:</strong> {patient.patientId}
                              </PatientDetailItem>
                              {patient.hospitalId && (
                                <PatientDetailItem>
                                  <strong>Hospital ID:</strong> {patient.hospitalId}
                                </PatientDetailItem>
                              )}
                              <PatientDetailItem style={{ color: '#3b82f6', fontWeight: 500 }}>
                                📅 Registered: {format(new Date(patient.registeredDate || patient.createdAt), 'MMM dd, yyyy')}
                              </PatientDetailItem>
                              {patient.priorityScore > 0 && (
                                <PatientDetailItem style={{ color: '#059669', fontWeight: 600 }}>
                                  <strong>Priority Score:</strong> {Math.round(patient.priorityScore)}
                                </PatientDetailItem>
                              )}
                            </PatientDetails>
                          </PatientInfo>
                          <PatientActions>
                            {canPrescribe && (
                              <MedicationButton onClick={() => handleOpenMedication(patient)}>
                                💊 Medication
                              </MedicationButton>
                            )}
                            {canMessage && (
                              <MessageButton onClick={() => handleOpenMessage(patient)}>
                                💬 Message
                              </MessageButton>
                            )}
                            <ViewButton onClick={() => handleViewPatient(patient)}>
                              📋 View Logs
                            </ViewButton>
                          </PatientActions>
                        </PatientCard>
                      );
                    })}
                </PatientList>
              ) : (
                <EmptyState>
                  <EmptyIcon>🔴</EmptyIcon>
                  <EmptyTitle>No priority patients</EmptyTitle>
                  <EmptyText>
                    {searchTerm 
                      ? 'No patients match your search criteria' 
                      : 'All patients with activity have been addressed. Patients will appear here when they submit pain logs or distress data and need attention.'}
                  </EmptyText>
                </EmptyState>
              )}
            </SectionCard>
          )}

        </>
      )}

      {selectedPatient && (
        <PatientModal onClick={() => setSelectedPatient(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedPatient.fullName}
              </ModalTitle>
              <CloseButton onClick={() => setSelectedPatient(null)}>Close</CloseButton>
            </ModalHeader>
            
            <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
              Patient ID: {selectedPatient.patientId} • 
              {getPatientAge(selectedPatient) !== null ? ` Age: ${getPatientAge(selectedPatient)}` : ''} • 
              {selectedPatient.phone ? ` Phone: ${selectedPatient.phone}` : ''}
            </div>

            <PatientTabsContainer>
              <PatientTab active={patientViewTab === 'logs'} onClick={() => setPatientViewTab('logs')}>
                📋 Pain Logs
              </PatientTab>
              <PatientTab active={patientViewTab === 'history'} onClick={() => setPatientViewTab('history')}>
                📚 History
              </PatientTab>
              {canSeeDistress && (
                <PatientTab active={patientViewTab === 'distress'} onClick={() => setPatientViewTab('distress')}>
                  🌡️ Distress History
                </PatientTab>
              )}
              <PatientTab active={patientViewTab === 'stats'} onClick={() => setPatientViewTab('stats')}>
                📈 Statistics
              </PatientTab>
              {canPrescribe && (
                <PatientTab active={patientViewTab === 'doses'} onClick={() => setPatientViewTab('doses')}>
                  💊 Doses taken
                </PatientTab>
              )}
            </PatientTabsContainer>
            
            <TabContentWrapper>
              {patientViewTab === 'logs' && selectedPatient?.patientId && (
                <TabContent key="logs">
                  <PainLogs patientId={selectedPatient.patientId} compact={true} />
                </TabContent>
              )}
              {patientViewTab === 'logs' && !selectedPatient?.patientId && (
                <TabContent key="logs-error">
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
                    Error: Patient ID not found. Please try again.
                  </div>
                </TabContent>
              )}

              {patientViewTab === 'history' && (
                <TabContent key="history">
                  <History patientId={selectedPatient?.patientId} compact={true} />
                </TabContent>
              )}

              {patientViewTab === 'distress' && canSeeDistress && (
                <TabContent key="distress">
                  <DistressHistory patientId={selectedPatient?._id} compact={true} />
                </TabContent>
              )}

              {patientViewTab === 'stats' && (
                <TabContent key="stats">
                  <Statistics patientId={selectedPatient?.patientId} compact={true} />
                </TabContent>
              )}

              {patientViewTab === 'doses' && canPrescribe && (
                <TabContent key="doses">
                  {dosesTaken.length === 0 && <div style={{ padding: '1rem', color: '#6b7280' }}>No doses recorded yet.</div>}
                  {dosesTaken.map((dose) => (
                    <div key={dose.id} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                      <strong>{dose.medicationName}</strong>
                      <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>{new Date(dose.takenAt).toLocaleString()}</div>
                    </div>
                  ))}
                </TabContent>
              )}
            </TabContentWrapper>
          </ModalContent>
        </PatientModal>
      )}

      {messagePatient && (
        <MessageModal onClick={handleCloseMessage}>
          <MessageModalContent onClick={(e) => e.stopPropagation()}>
            <MessageModalHeader>
              <MessageModalTitle>
                Message to {messagePatient.fullName}
              </MessageModalTitle>
              <MessageCloseButton onClick={handleCloseMessage}>✕</MessageCloseButton>
            </MessageModalHeader>

            <MessageForm onSubmit={handleSendMessage}>
              <FormGroup>
                <FormLabel>Message Type</FormLabel>
                <FormSelect
                  value={messageForm.type}
                  onChange={(e) => setMessageForm({ ...messageForm, type: e.target.value })}
                  required
                >
                  <option value="general">General</option>
                  <option value="appointment">Appointment</option>
                  <option value="advice">Advice</option>
                  <option value="instruction">Instruction</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>Subject (Optional)</FormLabel>
                <FormInput
                  type="text"
                  placeholder="e.g., Follow-up instructions"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  maxLength={200}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Message *</FormLabel>
                <FormTextarea
                  placeholder="Enter your message here..."
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                  maxLength={2000}
                />
              </FormGroup>

              <FormActions>
                <CancelButton type="button" onClick={handleCloseMessage}>
                  Cancel
                </CancelButton>
                <SendButton type="submit" disabled={sendingMessage || !messageForm.message.trim()}>
                  {sendingMessage ? 'Sending...' : 'Send Message'}
                </SendButton>
              </FormActions>
            </MessageForm>
          </MessageModalContent>
        </MessageModal>
      )}

      {medicationPatient && (
        <MedicationPrescriptionModal
          patient={medicationPatient}
          doctor={doctorUser}
          onClose={handleCloseMedication}
        />
      )}


      </MainContent>
    </DashboardContainer>
  );
};

export default DoctorDashboard;
